import { Nullable } from '@/Types';
import { IPrinterInfo, IPrintService, PrinterSettings, PrintResult, TemplateSettings } from './IPrintService';
import { print as pdfPrint } from 'pdf-to-printer';
import { BinaryResponse } from '@/Api';
import { PrintOptions } from 'pdf-to-printer/dist/print/print';
import { FileSystemService } from '@/Electron/services/FileSystemService/FileSystemService';
import { documentStoreService } from '@/Electron/services/DocumentStoreService/ElectronDocumentStoreService';
import { electronUtils } from '@/Electron/ElectronImports';
import { Toasts } from '@/Components/Toast/Toast';
import { PrintUnionDocumentRequest } from '@/Api/DocumentTemplates/Requests/PrintUnionDocumentRequest';
import { DocumentTemplateApi } from '@/Api/DocumentTemplates/DocumentTemplatesApi';

class ElectronPrintService implements IPrintService {
  private readonly _fileSystem: FileSystemService;

  constructor() {
    this._fileSystem = new FileSystemService();
  }

  getPrinters(): IPrinterInfo[] {
    return electronUtils.getWebContents()?.getPrinters() ?? [];
  }

  getDefaultPrinter(): Nullable<IPrinterInfo> {
    const printer = this.getPrinters().find((x) => x.isDefault);
    return printer ? printer : null;
  }

  async printHandler(
    request: PrintUnionDocumentRequest,
    templateGuid: string,
    callback?: (params: number[]) => void,
  ): Promise<PrintResult> {
    try {
      const printerSettings = this.getPrinterSettings(templateGuid);
      const templateSettings = await this.getTemplateSettings(templateGuid);
      const documentResponse = await DocumentTemplateApi.printUnionDocument(templateGuid, request);

      callback?.(request.orderIds);

      if (templateSettings.isSaveWhenPrinting) {
        this.saveFiles(templateGuid, request.orderIds, templateSettings);
      }

      if (printerSettings.isPreview) {
        return this.openInWindow(documentResponse);
      }

      return this.printFile(documentResponse, printerSettings.printerName, templateSettings);
    } catch (error) {
      return PrintResult.ErrorPrint;
    }
  }

  saveFiles(guid: string, orderIds: number[], templateSettings: TemplateSettings) {
    try {
      if (templateSettings.isSaveWhenPrinting) {
        const isCorrectPath = this._fileSystem.isPathExists(templateSettings.directoryPath);

        if (isCorrectPath) {
          orderIds.forEach((id) => {
            DocumentTemplateApi.printDocument(guid, id).then((response) => {
              this._fileSystem.saveFile(response, templateSettings.directoryPath);
            });
          });
        } else {
          Toasts.showError({
            title: 'Save files',
            text: 'Save path does not exist. Please fix document template settings',
          });
        }
      }
    } catch (error) {
      return PrintResult.ErrorSaving;
    }
  }

  async printDocument(file: BinaryResponse, templateGuid: string): Promise<PrintResult> {
    try {
      const printerSettings = this.getPrinterSettings(templateGuid);
      const templateSettings = await this.getTemplateSettings(templateGuid);

      if (templateSettings.isSaveWhenPrinting) {
        const isCorrectPath = this._fileSystem.isPathExists(templateSettings.directoryPath);

        if (isCorrectPath) {
          this._fileSystem.saveFile(file, templateSettings.directoryPath);
        } else {
          Toasts.showError({
            title: 'Save files',
            text: 'Save path does not exist. Please fix document template settings',
          });
        }
      }

      if (printerSettings.isPreview) {
        return this.openInWindow(file);
      }

      return this.printFile(file, printerSettings.printerName);
    } catch (error) {
      return PrintResult.ErrorPrint;
    }
  }

  async printLabel(file: string, isPrintPreview: boolean, assignPrinter: string): Promise<PrintResult> {
    try {
      if (isPrintPreview) {
        const result = this.openInWindow(file);
        return Promise.resolve(result);
      }

      return this.printFile(file, assignPrinter);
    } catch (error) {
      return PrintResult.ErrorPrint;
    }
  }

  getPrinterSettings(templateGuid: string): PrinterSettings {
    const documentPrintInfo = documentStoreService.getByDocumentGuid(templateGuid);

    return {
      printerName: documentPrintInfo?.printerName ?? '',
      isPreview: documentPrintInfo?.isPreview ?? false,
    };
  }

  async getTemplateSettings(templateGuid: string): Promise<TemplateSettings> {
    const templateSettings = await DocumentTemplateApi.printerSettings(templateGuid);

    return {
      isSaveWhenPrinting: templateSettings.isSaveWhenPrinting,
      directoryPath: templateSettings.directoryPath ?? '',
      orientation: templateSettings?.isLandscape ? 'landscape' : undefined,
      scale: templateSettings?.scale?.toLocaleLowerCase() ?? undefined,
      silent: true,
      //paperSize: templateSettings?.paperKind ?? undefined
    };
  }

  printFile(
    file: BinaryResponse | string,
    printerName: string,
    templateSettings?: TemplateSettings,
  ): Promise<PrintResult> {
    const result = this.normalizeFile(file);
    const defaultPrinter = this.getDefaultPrinter();
    const actualPrinter = printerName ?? defaultPrinter?.name;

    const tempPath = this._fileSystem.getTempFolderPath();
    const filePath = this._fileSystem.saveFile(result, tempPath);

    if (!actualPrinter) {
      return Promise.resolve(PrintResult.ErrorPrint);
    }

    if (filePath === '') {
      return Promise.resolve(PrintResult.ErrorSaving);
    }

    const options: PrintOptions = {
      printer: printerName,
      ...templateSettings,
    };

    return pdfPrint(filePath, options)
      .then(() => {
        this._fileSystem.deleteFile(filePath);
        return PrintResult.Success;
      })
      .catch(() => {
        this._fileSystem.deleteFile(filePath);
        return PrintResult.ErrorPrint;
      });
  }

  normalizeFile(file: BinaryResponse | string): BinaryResponse {
    if (typeof file === 'object') {
      return file;
    } else {
      return {
        contentType: '',
        fileName: '',
        data: Buffer.from(file, 'base64'),
      };
    }
  }

  openInWindow(file: BinaryResponse | string): PrintResult {
    try {
      const result = this.normalizeFile(file);
      const blob = new Blob([result.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      window.open(url);
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);

      return PrintResult.Success;
    } catch (error) {
      return PrintResult.ErrorPrint;
    }
  }
}

export const printService = new ElectronPrintService();
